import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PublicLayout, Eyebrow } from "@/components/public/PublicKit";
import { CheckCircle2, Loader2, ArrowRight, Shield } from "lucide-react";

const EMPTY = {
  organization_name: "", contact_name: "", business_email: "", phone: "",
  organization_type: "", services_interest: [], consent: false,
};
const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

export default function BookDemo() {
  const [meta, setMeta] = useState({ org_types: [], services: [] });
  const [f, setF] = useState(EMPTY);
  const [err, setErr] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/public/demo-meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const toggleService = (s) =>
    setF((p) => ({ ...p, services_interest: p.services_interest.includes(s) ? p.services_interest.filter((x) => x !== s) : [...p.services_interest, s] }));

  const validate = () => {
    const e = {};
    if (!f.organization_name.trim()) e.organization_name = "Organization name is required";
    if (!f.contact_name.trim()) e.contact_name = "Contact name is required";
    if (!f.business_email.trim()) e.business_email = "Business email is required";
    else if (!emailOk(f.business_email.trim())) e.business_email = "Enter a valid email";
    if (!f.consent) e.consent = "Please allow us to contact you";
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (submitting || done) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/public/demo-request", {
        ...f, organization_name: f.organization_name.trim(),
        contact_name: f.contact_name.trim(), business_email: f.business_email.trim().toLowerCase(),
      });
      setDone(r.data);
    } catch (e) {
      setErr({ form: formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <PublicLayout>
      <section className="relative overflow-hidden border-b border-slate-100 bg-slate-50">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-emerald-100/60 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-[1fr_1.1fr] lg:px-8">
          <div className="lg:pr-8">
            <Eyebrow>Book a Demo</Eyebrow>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">See HudlUp on your workflow</h1>
            <p className="mt-4 max-w-md text-lg text-slate-500">Tell us a little about your organization and the services you're activating. A member of our team will reach out to schedule a walkthrough.</p>
            <ul className="mt-6 space-y-2.5">
              {["Connect provider acquisition to service activation", "Track 340B, RPM and infusion referrals", "See onboarding and client status in one place"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-slate-600"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8">
            {done ? (
              <div className="py-8 text-center" data-testid="bd-success">
                <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-8 w-8" /></div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Thank you — we've got it!</h2>
                <p className="mx-auto mt-2 max-w-sm text-slate-500">Your request has been received{done.lead_number ? <> (reference <span className="font-mono font-semibold text-slate-700">{done.lead_number}</span>)</> : ""}. Our team will be in touch at <span className="font-semibold text-slate-700">{f.business_email}</span>.</p>
              </div>
            ) : (
              <>
                <h2 className="mb-1 font-display text-xl font-bold text-slate-900">Request your demo</h2>
                <p className="mb-5 text-sm text-slate-500">Fields marked * are required.</p>
                {err.form && <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-600" data-testid="bd-error">{err.form}</p>}

                <div className="grid gap-4">
                  <L label="Organization name *" error={err.organization_name}>
                    <input data-testid="bd-org" className={inp} value={f.organization_name} onChange={(e) => setF({ ...f, organization_name: e.target.value })} placeholder="Acme Health Partners" />
                  </L>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <L label="Contact name *" error={err.contact_name}>
                      <input data-testid="bd-contact" className={inp} value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Jane Doe" />
                    </L>
                    <L label="Phone (optional)">
                      <input data-testid="bd-phone" className={inp} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(203) 555-1234" />
                    </L>
                  </div>
                  <L label="Business email *" error={err.business_email}>
                    <input data-testid="bd-email" type="email" className={inp} value={f.business_email} onChange={(e) => setF({ ...f, business_email: e.target.value })} placeholder="jane@organization.com" />
                  </L>
                  <L label="Organization type">
                    <select data-testid="bd-orgtype" className={inp} value={f.organization_type} onChange={(e) => setF({ ...f, organization_type: e.target.value })}>
                      <option value="">Select type…</option>
                      {meta.org_types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </L>
                  <div>
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Services of interest</span>
                    <div className="flex flex-wrap gap-2">
                      {meta.services.map((s) => {
                        const on = f.services_interest.includes(s);
                        return (
                          <button type="button" key={s} data-testid={`bd-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleService(s)}
                            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${on ? "border-primary bg-primary text-white" : "border-slate-300 text-slate-600 hover:border-primary/50"}`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <label className="flex items-start gap-2.5">
                    <input data-testid="bd-consent" type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
                    <span className="text-sm text-slate-600">I agree to be contacted about this request.{err.consent && <span className="ml-1 font-semibold text-red-600">{err.consent}</span>}</span>
                  </label>
                  <button data-testid="bd-submit" onClick={submit} disabled={submitting}
                    className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-bold text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60">
                    {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</> : <>Request Demo <ArrowRight className="h-5 w-5" /></>}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><Shield className="h-3.5 w-3.5" /> We'll only use your details to contact you about HudlUp.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function L({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>}
    </label>
  );
}
